import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCalendarData } from './CalendarDataContext';
import ReportProblem from './ReportProblem';
import StateSummaryDisclaimer from './StateSummaryDisclaimer';
import { countyLabel } from '../../utils/countyDisplay';

/**
 * One state: how its sales work, then every county we have entered for it.
 *
 * The summary at the top is the part a subscriber reads once and the part that
 * decides whether they bid at all: what kind of sale it is, whether there is a
 * redemption period, what the interest or penalty is, whether there is an over
 * the counter route, and the statute to check it against.
 *
 * A state that is not live yet still has a page, because the state picker links
 * to it. It shows the summary if the owner has written one and says plainly that
 * the counties are not entered yet, rather than rendering an empty table.
 */

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--color-text-muted)',
        marginBottom: '2px'
      }}>
        {label}
      </div>
      <div style={{ color: 'var(--color-text)' }}>{value}</div>
    </div>
  );
}

/**
 * =============================================================================
 * WHY THE COUNTY LIST IS GROUPED RATHER THAN LISTED
 * =============================================================================
 * Wisconsin has 72 counties and 53 of them have nothing entered. Texas has 254.
 * Listed alphabetically, the 19 counties a subscriber is paying to know about
 * sit scattered between four screens of "No sale dates entered yet", and the
 * page reads as mostly empty when it is mostly full.
 *
 * So the counties that have something come first, soonest first, and the rest
 * collapse into one line that opens in place. NOTHING IS REMOVED: every county
 * is still on the page and still links to its own page, because a county with
 * no sale entered is exactly the page a reader goes to for the phone number.
 *
 * Pagination was considered and rejected. A reader looking for one county does
 * not know which page it is on, which is why there is a search box instead, and
 * why the search opens the collapsed group rather than filtering around it.
 */

/** Sortable position of one sale: which bucket, and how soon inside it. */
function saleOrder(sale, currentMonth) {
  if (sale.nextSaleDate) {
    const at = new Date(sale.nextSaleDate).getTime();
    if (!Number.isNaN(at)) return { rank: 0, value: at };
  }
  const month = Number(sale.typicalMonth);
  if (Number.isInteger(month) && month >= 1 && month <= 12) {
    // Calendar order starting from the month we are in, so a September sale
    // read in September sorts ahead of one in the following March.
    return { rank: 1, value: (month - currentMonth + 12) % 12 };
  }
  return { rank: 2, value: 0 };
}

/** The soonest thing a county has, which is what the county sorts on. */
function countyOrder(county, currentMonth) {
  let best = null;
  for (const sale of county.sales || []) {
    const order = saleOrder(sale, currentMonth);
    if (!best || order.rank < best.rank || (order.rank === best.rank && order.value < best.value)) {
      best = order;
    }
  }
  return best;
}

/**
 * A county name reduced to the part a reader types.
 *
 * "with or without the word County" is the whole point: somebody looking for
 * St. Croix County types "st croix", and somebody looking at a Louisiana page
 * types "parish" or does not. Both have to land.
 */
function searchKey(value) {
  return String(value || '')
    .toLowerCase()
    // Apostrophes are DELETED rather than turned into a space, so that
    // Prince George's answers to "prince georges", which is how it is typed.
    .replace(/['‘’]/g, '')
    .replace(/\b(county|parish|borough|census area|municipality|city and borough)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function matchesSearch(county, query) {
  if (!query) return true;
  const needle = searchKey(query);
  if (!needle) return true;
  return searchKey(county.county_name).includes(needle);
}

/**
 * The two groups, in the order they are read.
 *
 * A TOP LEVEL FUNCTION, not a body inside the component, so check:calendar can
 * lift it out and run it against fixtures. The ordering is the whole fix: a
 * version of it that quietly sorted alphabetically again would render a page
 * that looks correct and buries exactly what a subscriber came for.
 */
function groupCounties(counties, currentMonth) {
  const scheduled = [];
  const quiet = [];

  for (const county of counties || []) {
    const order = countyOrder(county, currentMonth);
    if (order) scheduled.push({ county, order });
    else quiet.push(county);
  }

  scheduled.sort((a, b) => (
    a.order.rank - b.order.rank
    || a.order.value - b.order.value
    || a.county.county_name.localeCompare(b.county.county_name)
  ));
  quiet.sort((a, b) => a.county_name.localeCompare(b.county_name));

  return { withSales: scheduled.map(entry => entry.county), withoutSales: quiet };
}

function CountyRow({ county, path, showSales }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}>
      <Link
        to={path(`/counties/${county.id}`)}
        style={{ color: 'var(--color-heading)', fontWeight: 700, textDecoration: 'none' }}
      >
        {countyLabel(county.county_name)}
      </Link>

      {county.verified_at && (
        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem', marginLeft: '8px' }}>
          Last verified {new Date(county.verified_at).toLocaleDateString()}
        </span>
      )}

      {showSales && (county.sales || []).map(sale => (
        <div key={sale.id} style={{ color: 'var(--color-text-light)', fontSize: '0.88rem', marginTop: '4px' }}>
          {sale.dateLabel}: {[sale.saleKindLabel, sale.platformLabel].filter(Boolean).join(', ')}
          {sale.listUrl && (
            <>
              {' '}
              <a href={sale.listUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                list
              </a>
            </>
          )}
        </div>
      ))}

      {showSales && (county.sales || []).length === 0 && (
        <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
          No sale dates entered yet.
        </div>
      )}
    </div>
  );
}

function CountyList({ counties, path }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);

  const { withSales, withoutSales } = useMemo(
    () => groupCounties(counties, new Date().getUTCMonth() + 1),
    [counties]
  );

  const searching = query.trim() !== '';
  const shownWithSales = useMemo(
    () => withSales.filter(county => matchesSearch(county, query)),
    [withSales, query]
  );
  const shownWithoutSales = useMemo(
    () => withoutSales.filter(county => matchesSearch(county, query)),
    [withoutSales, query]
  );

  // A search that matched something inside the collapsed group would otherwise
  // hide its own results behind a closed expander, so the search opens it.
  const quietOpen = expanded || searching;
  const matchCount = shownWithSales.length + shownWithoutSales.length;
  const noCounties = counties.length === 0;

  if (noCounties) {
    return (
      <p style={{ color: 'var(--color-text-light)' }}>
        No counties are entered for this state yet.
      </p>
    );
  }

  return (
    <div>
      <div style={{ margin: '4px 0 12px' }}>
        <label
          htmlFor="county-search"
          style={{
            display: 'block',
            fontSize: '0.78rem',
            color: 'var(--color-text-muted)',
            marginBottom: '4px'
          }}
        >
          Search counties
        </label>
        <input
          id="county-search"
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Start typing a county name"
          style={{
            width: '100%',
            maxWidth: '360px',
            boxSizing: 'border-box',
            padding: '8px 10px',
            borderRadius: '6px',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
            fontSize: '0.92rem'
          }}
        />
        <div aria-live="polite" style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginTop: '6px' }}>
          {searching
            ? `${matchCount} ${matchCount === 1 ? 'county matches' : 'counties match'} ${query.trim()}`
            : ''}
        </div>
      </div>

      {searching && matchCount === 0 && (
        <p style={{ color: 'var(--color-text-light)' }}>
          No county here matches that. Check the spelling, or clear the search to see all {counties.length}.
        </p>
      )}

      {shownWithSales.length > 0 && (
        <>
          <h3 style={{ fontSize: '0.92rem', color: 'var(--color-text-muted)', margin: '0 0 2px', fontWeight: 600 }}>
            Counties with sale dates
          </h3>
          {shownWithSales.map(county => (
            <CountyRow key={county.id} county={county} path={path} showSales />
          ))}
        </>
      )}

      {withoutSales.length > 0 && (
        <div style={{ marginTop: shownWithSales.length > 0 ? '20px' : '0' }}>
          <button
            type="button"
            onClick={() => setExpanded(open => !open)}
            aria-expanded={quietOpen}
            aria-controls="counties-without-sales"
            style={{
              width: '100%',
              textAlign: 'left',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              padding: '10px 12px',
              color: 'var(--color-text)',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            {quietOpen ? 'Hide' : 'Show'} {withoutSales.length}{' '}
            {withoutSales.length === 1 ? 'county' : 'counties'} with no sale dates yet
          </button>

          <div id="counties-without-sales" hidden={!quietOpen}>
            {shownWithoutSales.map(county => (
              <CountyRow key={county.id} county={county} path={path} showSales={false} />
            ))}
            {quietOpen && searching && shownWithoutSales.length === 0 && (
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginTop: '10px' }}>
                None of these match that search.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarState() {
  const { api, path } = useCalendarData();
  const { code } = useParams();
  const [state, setState] = useState(null);
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const data = await api(`/states/${encodeURIComponent(code)}`);
        if (cancelled) return;
        setState(data.state);
        setCounties(data.counties || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [code, api]);

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading</p>;
  if (error) return <p style={{ color: 'var(--color-danger-text, var(--color-text))' }}>{error}</p>;
  if (!state) return null;

  const hasSummary = [
    state.sale_type_label, state.redemption_summary, state.interest_or_penalty_summary,
    state.otc_route_summary, state.primary_statute_citation, state.notes
  ].some(Boolean);

  return (
    <div>
      <Link to={path('/')} style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
        Back to upcoming sales
      </Link>

      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.35rem', margin: '12px 0 4px' }}>
        {state.name}
      </h1>

      {!state.in_scope && (
        <p style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '6px',
          padding: '12px',
          color: 'var(--color-text-light)'
        }}>
          Coming soon. We are researching this state now, so the county by county
          dates are not entered yet. Anything below is what we have confirmed so far.
        </p>
      )}

      <section style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '16px'
      }}>
        <Field label="Sale type" value={state.sale_type_label} />
        <Field label="Redemption" value={state.redemption_summary} />
        <Field label="Interest or penalty" value={state.interest_or_penalty_summary} />
        <Field label="Over the counter" value={state.otc_route_summary} />
        <Field label="Statute" value={state.primary_statute_citation} />
        <Field label="Notes" value={state.notes} />

        {!state.sale_type_label && !state.redemption_summary && (
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            We have not published a summary for this state yet.
          </p>
        )}

        {hasSummary && <StateSummaryDisclaimer verifiedAt={state.verified_at} />}
      </section>

      <h2 style={{ fontSize: '1.05rem', color: 'var(--color-heading)', marginTop: '28px' }}>
        Counties
      </h2>

      <CountyList counties={counties} path={path} />

      <ReportProblem page={`state:${code}`} stateCode={code} />
    </div>
  );
}

export default CalendarState;
